import type { QueryClient } from '@tanstack/react-query'

/**
 * Invalide toutes les vues qui montrent des fiches d'un espace.
 *
 * Existe parce que l'oubli s'est produit trois fois : une fiche ajoutée depuis une catégorie
 * n'y apparaissait pas, et une note archivée restait affichée dans la liste — au point qu'il
 * fallait cliquer deux fois sur Archiver. Le cache est configuré à 30 s de fraîcheur et 60 s
 * entre deux rafraîchissements (App.tsx) : une liste non invalidée reste donc visiblement
 * fausse pendant une demi-minute.
 *
 * Une seule fonction plutôt qu'une liste de clés recopiée à chaque appel : ajouter un écran
 * qui liste des fiches se fait ici, une fois.
 */
export async function invaliderFiches(queryClient: QueryClient, spaceId: string) {
  await Promise.all([
    // Préfixe : couvre ['fiches', spaceId] comme ['fiches', spaceId, categorieId].
    queryClient.invalidateQueries({ queryKey: ['fiches', spaceId] }),
    // Préfixe : couvre chaque fiche de l'espace, sans avoir à connaître son identifiant.
    queryClient.invalidateQueries({ queryKey: ['fiche', spaceId] }),
    // Couvre aussi ['espace', spaceId, 'categories'] par préfixe.
    queryClient.invalidateQueries({ queryKey: ['espace', spaceId] }),
    queryClient.invalidateQueries({ queryKey: ['aujourdhui'] }),
  ])
}

/**
 * Invalide toutes les vues qui montrent de l'argent.
 *
 * Même raison que ci-dessus, et le même oubli est déjà arrivé : une dépense notée depuis une
 * fiche ne mettait pas à jour le solde annoncé sur la tuile de l'espace, et une dépense
 * corrigée depuis l'écran des dépenses laissait la fiche afficher l'ancien montant. Trois
 * écrans lisent la même somme ; ils doivent être rafraîchis ensemble.
 */
export async function invaliderDepenses(queryClient: QueryClient, spaceId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['depenses', spaceId] }),
    // Préfixe : couvre chaque fiche de l'espace, sans connaître son identifiant.
    queryClient.invalidateQueries({ queryKey: ['depenses-fiche', spaceId] }),
    // La tuile de l'espace annonce le solde.
    queryClient.invalidateQueries({ queryKey: ['espace', spaceId] }),
    // Régler une période la fait entrer dans l'historique : sans cette ligne, il resterait vide
    // jusqu'au prochain chargement de l'écran.
    queryClient.invalidateQueries({ queryKey: ['reglements', spaceId] }),
  ])
}

/**
 * Invalide la liste de courses et ses listes passées.
 *
 * Vider le panier fait passer des articles de l'une à l'autre : rafraîchir la seule liste
 * courante laisserait les listes passées sans celle qu'on vient de clore.
 */
export async function invaliderCourses(queryClient: QueryClient, spaceId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['courses', spaceId] }),
    queryClient.invalidateQueries({ queryKey: ['courses-historique', spaceId] }),
    queryClient.invalidateQueries({ queryKey: ['espace', spaceId] }),
    queryClient.invalidateQueries({ queryKey: ['aujourdhui'] }),
  ])
}
